import { useCallback, useEffect, useState } from 'react';

type NavigationMenu = {
  isOpen: boolean;
  toggle: () => void;
  close: () => void;
};

/**
 * Open/closed state for the slide-out menu, with the behaviour a drawer is
 * expected to have: Escape closes it, and navigating away closes it too.
 *
 * `closeKey` is any value that changes on navigation (the pathname).
 */
export const useNavigationMenu = (closeKey: string): NavigationMenu => {
  const [isOpen, setIsOpen] = useState(false);

  const close = useCallback(() => setIsOpen(false), []);
  const toggle = useCallback(() => setIsOpen((open) => !open), []);

  useEffect(() => {
    setIsOpen(false);
  }, [closeKey]);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsOpen(false);
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  return { isOpen, toggle, close };
};
